import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, Alert, InputAdornment, Stack, Paper
} from '@mui/material';
import {
  ArrowBack, Receipt, Payment, ExitToApp, Print, CheckCircle
} from '@mui/icons-material';
import { salesAPI, invoicesAPI } from '../services/api';
import api from '../services/api';
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

// Helper: format currency
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

// Status transition definitions
const STATUS_TRANSITIONS = {
  draft: [
    { value: 'confirmed', label: 'Confirm Order', color: 'primary' },
    { value: 'cancelled', label: 'Cancel', color: 'error' }
  ],
  confirmed: [
    { value: 'processing', label: 'Mark Processing', color: 'info' },
    { value: 'cancelled', label: 'Cancel', color: 'error' }
  ],
  processing: [
    { value: 'paid', label: 'Mark Paid (Final)', color: 'success' },
    { value: 'cancelled', label: 'Cancel', color: 'error' }
  ],
  paid: [],
  cancelled: [],
  returned: []
};

// ----------------------------- Payment Dialog -----------------------------
const PaymentDialog = ({ open, onClose, orderId, balanceDue, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (open) {
      api.get('/sales/payment-methods')
        .then(r => setPaymentMethods(r.data.data || []))
        .catch(() => setPaymentMethods([]));
    }
  }, [open]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const paymentAmount = parseFloat(data.amount);
      await salesAPI.recordPayment(orderId, { ...data, amount: paymentAmount });
      toast.success('Payment recorded successfully');
      reset();
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Record Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                {...register('amount', {
                  required: 'Amount required',
                  min: { value: 0.01, message: 'Must be greater than 0' },
                  max: { value: balanceDue, message: `Cannot exceed balance due of ${fmt(balanceDue)}` },
                  valueAsNumber: true
                })}
                error={!!errors.amount}
                helperText={errors.amount?.message || `Maximum allowed: ${fmt(balanceDue)}`}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select {...register('payment_method_id')} label="Payment Method" defaultValue="">
                  <MenuItem value="">Select method</MenuItem>
                  {paymentMethods.map(pm => (
                    <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Reference Number" {...register('reference_number')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<Payment />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ----------------------------- Exit Ticket Dialog -----------------------------
const ExitTicketDialog = ({ open, onClose, order }) => {
  const [loading, setLoading] = useState(false);

  const handlePrintTicket = () => {
    const ticketContent = `
      <div style="font-family: monospace; padding: 20px; max-width: 400px; margin: 0 auto;">
        <div style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px;">
          <h2 style="margin: 0;">EXIT PREMISES TICKET</h2>
          <p style="margin: 5px 0;">AUTHORIZED VEHICLE REMOVAL</p>
        </div>
        <div style="margin-bottom: 15px;">
          <p><strong>Ticket #:</strong> ${order.order_number}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>Time:</strong> ${new Date().toLocaleTimeString()}</p>
        </div>
        <div style="border: 1px solid #000; padding: 10px; margin-bottom: 15px;">
          <h4 style="margin: 0 0 10px 0;">CUSTOMER INFORMATION</h4>
          <p><strong>Name:</strong> ${order.customer_name}</p>
          <p><strong>Email:</strong> ${order.customer_email || 'N/A'}</p>
          <p><strong>Phone:</strong> ${order.customer_phone || 'N/A'}</p>
          <p><strong>Sales Person:</strong> ${order.salesperson_name || 'N/A'}</p>
        </div>
        <div style="border: 1px solid #000; padding: 10px; margin-bottom: 15px;">
          <h4 style="margin: 0 0 10px 0;">VEHICLE(S) TO REMOVE</h4>
          ${order.items.map(item => `
            <div>
              <strong>• ${item.product_name}</strong> (${item.quantity} unit(s))<br/>
              ${item.chassis_number ? `Chassis: ${item.chassis_number}` : ''}
              ${item.unit_color ? `, Color: ${item.unit_color}` : ''}
            </div>
          `).join('')}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 10px; text-align: center;">
          <p><strong>This ticket authorizes the removal of the above vehicle(s) from company premises.</strong></p>
          <p>Valid only on: ${new Date().toLocaleDateString()}</p>
          <div style="margin-top: 20px; border-top: 1px dashed #000; padding-top: 10px;">
            <p>_________________________</p>
            <p>Authorized Signature</p>
          </div>
        </div>
      </div>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Exit Premises Ticket - ${order.order_number}</title>
        <style>
          body { margin: 0; padding: 20px; font-family: monospace; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>${ticketContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadTicket = async () => {
    try {
      setLoading(true);
      const ticketText = `
EXIT PREMISES TICKET
==================
Ticket #: ${order.order_number}
Date: ${new Date().toLocaleDateString()}
Time: ${new Date().toLocaleTimeString()}

CUSTOMER INFORMATION:
Name: ${order.customer_name}
Email: ${order.customer_email || 'N/A'}
Phone: ${order.customer_phone || 'N/A'}
Sales Person: ${order.salesperson_name || 'N/A'}

VEHICLE(S) TO REMOVE:
${order.items.map(item => `- ${item.product_name} (Qty: ${item.quantity})${item.chassis_number ? ` - Chassis: ${item.chassis_number}` : ''}${item.unit_color ? `, Color: ${item.unit_color}` : ''}`).join('\n')}

Total Paid: ${fmt(order.total_amount)}
Payment Status: PAID IN FULL

This ticket authorizes the removal of the above vehicle(s) from company premises.
Valid only on: ${new Date().toLocaleDateString()}

_________________________
Authorized Signature
      `.trim();
      const blob = new Blob([ticketText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exit-ticket-${order.order_number}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Exit ticket downloaded successfully');
    } catch (err) {
      toast.error('Failed to download ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle fontWeight={700}>Exit Premises Ticket</DialogTitle>
      <DialogContent dividers>
        <Alert severity="success" sx={{ mb: 2 }}>
          ✓ Order has been paid in full – this ticket authorizes vehicle removal.
        </Alert>
        <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '2px solid #dee2e6' }}>
          <Typography variant="h6" fontWeight={700} textAlign="center">EXIT PREMISES TICKET</Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary" gutterBottom>
            AUTHORIZED VEHICLE REMOVAL
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography fontWeight={600}>Ticket #:</Typography>
              <Typography>{order.order_number}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={600}>Date:</Typography>
              <Typography>{new Date().toLocaleDateString()}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Customer:</Typography>
              <Typography>{order.customer_name}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Sales Person:</Typography>
              <Typography>{order.salesperson_name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Vehicle(s):</Typography>
              {order.items.map((item, idx) => (
                <Typography key={idx} variant="body2" sx={{ ml: 1 }}>
                  • {item.product_name} (Qty: {item.quantity})
                  {item.chassis_number && ` - Chassis: ${item.chassis_number}`}
                  {item.unit_color && `, Color: ${item.unit_color}`}
                </Typography>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Total Paid:</Typography>
              <Typography color="success.main">{fmt(order.total_amount)}</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" textAlign="center" fontStyle="italic">
            This ticket authorizes removal of the above vehicle(s) from company premises.
            Valid only on {new Date().toLocaleDateString()}.
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrintTicket}>
          Print Ticket
        </Button>
        <Button variant="contained" startIcon={<ExitToApp />} onClick={handleDownloadTicket} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Download Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------- Main Component -----------------------------
const SalesDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [exitTicketOpen, setExitTicketOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);
  const [approving, setApproving] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await salesAPI.getById(id);
      setOrder(data.data);
    } catch {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleStatusChange = async (status) => {
    // Validation for marking as paid
    if (status === 'paid' && order.balance_due > 0) {
      toast.error(`Cannot mark as paid: Outstanding balance of ${fmt(order.balance_due)} must be paid first.`);
      return;
    }
    try {
      setStatusLoading(status);
      await salesAPI.updateStatus(id, status);
      toast.success(`Order ${status}`);
      if (status === 'paid') {
        await fetchOrder();
        setExitTicketOpen(true);
      } else {
        await fetchOrder();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusLoading(null);
    }
  };

  const handleApproveOrder = async () => {
    try {
      setApproving(true);
      await salesAPI.approve(id);
      toast.success('Sales order approved');
      await fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve order');
    } finally {
      setApproving(false);
    }
  };

  const handleApproveExitTicket = async () => {
    if (!order) return;
    try {
      setApproving(true);
      await api.patch(`/sales/${id}/approve-exit-ticket`);
      toast.success('Exit ticket approved');
      await fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve exit ticket');
    } finally {
      setApproving(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      await invoicesAPI.create({ order_id: id });
      toast.success('Invoice generated');
    } catch (err) {
      toast.error('Failed to generate invoice');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!order) {
    return <Alert severity="error">Order not found</Alert>;
  }

  const isAdmin = user?.roles?.some((role) => ['admin', 'sp_admin'].includes(role));
  const approvalRequired = Boolean(order.approval_required);
  const isApproved = !approvalRequired || ['approved', 'not_required'].includes(order.approval_status);
  const canApprove = isAdmin && approvalRequired && !isApproved && !['cancelled', 'returned', 'paid'].includes(order.status);

  const transitions = (STATUS_TRANSITIONS[order.status] || []).filter((transition) => {
    if (transition.value === 'paid' && !isAdmin) return false;
    if (transition.value === 'processing' && approvalRequired && !isApproved && !isAdmin) return false;
    return true;
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales')}>
          Back
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            Order {order.order_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(order.order_date).toLocaleString()}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusChip status={order.approval_status || (approvalRequired ? 'pending_approval' : 'not_required')} />
          <StatusChip status={order.status} />
        </Stack>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {transitions.map(t => (
          <Button
            key={t.value}
            variant="contained"
            color={t.color || 'primary'}
            disabled={statusLoading === t.value}
            onClick={() => handleStatusChange(t.value)}
          >
            {statusLoading === t.value
              ? <CircularProgress size={20} />
              : (
                t.value === 'processing' && approvalRequired && !isApproved && isAdmin
                  ? 'Approve & Mark Processing'
                  : t.label
              )}
          </Button>
        ))}
        {['confirmed', 'processing', 'delivered'].includes(order.status) && order.balance_due > 0 && !(approvalRequired && !isApproved) && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Payment />}
            onClick={() => setPaymentOpen(true)}
          >
            Record Payment
          </Button>
        )}
        {order.status === 'paid' && (
          <>
            {isAdmin && !order.exit_ticket_approved_by && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<CheckCircle />}
                onClick={handleApproveExitTicket}
                disabled={approving}
              >
                {approving ? <CircularProgress size={20} /> : 'Approve Exit Ticket'}
              </Button>
            )}
            {(order.exit_ticket_approved_by || isAdmin) && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ExitToApp />}
                onClick={() => setExitTicketOpen(true)}
              >
                Exit Ticket
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Receipt />}
              onClick={handleGenerateInvoice}
            >
              Generate Invoice
            </Button>
          </>
        )}
      </Stack>

      <Grid container spacing={3}>
        {/* LEFT COLUMN: Order Items & Payments */}
        <Grid item xs={12} md={8}>
          {/* Order Items Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Order Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>VIN/Chassis</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell fontWeight={600}>{item.product_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {item.sku}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                        {item.chassis_number || 'N/A'}
                      </TableCell>
                      <TableCell>{item.model || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.unit_condition || 'N/A'}
                          size="small"
                          color={
                            item.unit_condition === 'Excellent' ? 'success' :
                            item.unit_condition === 'Good' ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{fmt(item.unit_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {fmt(item.line_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Box sx={{ minWidth: 240 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{fmt(order.subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Discount ({order.discount_percent}%):</Typography>
                    <Typography variant="body2" color="success.main">
                      -{fmt(order.discount_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Tax ({order.tax_percent}%):</Typography>
                    <Typography variant="body2">{fmt(order.tax_amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Shipping:</Typography>
                    <Typography variant="body2">{fmt(order.shipping_cost)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontWeight={700}>Total:</Typography>
                    <Typography fontWeight={700} color="primary">
                      {fmt(order.total_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="success.main">Paid:</Typography>
                    <Typography variant="body2" color="success.main">
                      {fmt(order.paid_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="error.main" fontWeight={700}>
                      Balance Due:
                    </Typography>
                    <Typography variant="body2" color="error.main" fontWeight={700}>
                      {fmt(order.balance_due)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Payment History */}
          {(order.payments || []).length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Payment History
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {p.payment_number}
                        </TableCell>
                        <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                        <TableCell>{p.payment_method_name || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {fmt(p.amount)}
                        </TableCell>
                        <TableCell><StatusChip status={p.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* RIGHT COLUMN: Customer, Salesperson, Unit Details, Notes */}
        <Grid item xs={12} md={4}>
          {/* Customer Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Customer
              </Typography>
              <Typography fontWeight={600}>{order.customer_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {order.customer_email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order.customer_phone}
              </Typography>
            </CardContent>
          </Card>

          {/* Salesperson Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Salesperson
              </Typography>
              <Typography>{order.salesperson_name || 'N/A'}</Typography>
            </CardContent>
          </Card>

          {/* Unit Details Card - FIXED: now uses data directly from order.items */}
          {(order.items || []).length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Unit Details
                </Typography>
                {order.items.map((item) => (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      {item.product_name} ({item.quantity} unit{item.quantity > 1 ? 's' : ''})
                    </Typography>
                    {item.unit_product_id ? (
                      <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Unit Details
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>VIN/Chassis:</strong> {item.chassis_number || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Model:</strong> {item.model || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Condition:</strong> {item.unit_condition || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Engine:</strong> {item.engine_number || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Color:</strong> {item.unit_color || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Year:</strong> {item.unit_year || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Mileage:</strong> {item.mileage || 'N/A'}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No specific unit was assigned to this item.
                      </Typography>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Notes Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order.notes || 'No notes'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        orderId={id}
        balanceDue={order.balance_due}
        onSaved={fetchOrder}
      />
      <ExitTicketDialog
        open={exitTicketOpen}
        onClose={() => setExitTicketOpen(false)}
        order={order}
      />
    </Box>
  );
};

export default SalesDetailPage;
